import { useState, useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import { shiftQueryKeys } from '@/hooks/scheduling/useShiftsQuery';
import type { AIMessage, AIResponse, AIConversationSummary, AIUsage, AIToolCall, AIToolResult, PendingTool, AIQuestionAnswers, StreamEvent } from './types';

// AI endpoint configuration
const AI_ENDPOINT = import.meta.env.VITE_AI_ENDPOINT || 'http://localhost:3001/api/ai-chat';

// Helper to generate conversation title from first message
const generateTitle = (content: string): string => {
  const maxLength = 40;

  // Clean up the message
  let cleaned = content.trim().replace(/\s+/g, ' ');

  // Remove common question starters for cleaner titles
  const questionStarters = [
    /^(can you|could you|please|hey|hi|hello|i want to|i need to|i'd like to|show me|tell me|help me|what is|what are|what's|how do|how can|how is|how are)\s+/i,
    /^(give me|get me|find|search|look up|check)\s+/i,
  ];

  for (const pattern of questionStarters) {
    cleaned = cleaned.replace(pattern, '');
  }

  // Capitalize first letter
  cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);

  // Remove trailing punctuation for cleaner look
  cleaned = cleaned.replace(/[?.!,]+$/, '');

  if (cleaned.length <= maxLength) return cleaned;

  // Try to cut at word boundary
  const truncated = cleaned.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  if (lastSpace > maxLength * 0.6) {
    return truncated.substring(0, lastSpace) + '...';
  }

  return truncated + '...';
};

// Tools that modify data and require cache invalidation
const DATA_MODIFYING_TOOLS = {
  shifts: ['create_shift', 'delete_shift'],
  employees: ['create_employee', 'delete_employee'],
} as const;

// Helper to detect which resources were modified by tool calls
function getModifiedResources(toolCalls: AIToolCall[] | undefined): { shifts: boolean; employees: boolean } {
  if (!toolCalls) return { shifts: false, employees: false };

  const hasSuccessfulCall = (tools: readonly string[]) =>
    toolCalls.some(tc => tools.includes(tc.name) && tc.result?.success);

  return {
    shifts: hasSuccessfulCall(DATA_MODIFYING_TOOLS.shifts),
    employees: hasSuccessfulCall(DATA_MODIFYING_TOOLS.employees),
  };
}

// Tool display names for toast messages
const TOOL_DISPLAY_NAMES: Record<string, { action: string; resource: string }> = {
  create_shift: { action: 'created', resource: 'Shift' },
  delete_shift: { action: 'deleted', resource: 'Shift' },
  create_employee: { action: 'created', resource: 'Employee' },
  delete_employee: { action: 'deleted', resource: 'Employee' },
  list_employees: { action: 'fetched', resource: 'Employees' },
  get_shifts: { action: 'fetched', resource: 'Shifts' },
  get_reviews: { action: 'fetched', resource: 'Reviews' },
  get_business_stats: { action: 'fetched', resource: 'Business stats' },
  update_employee: { action: 'updated', resource: 'Employee' },
  get_employee_plan_fulfillment: { action: 'fetched', resource: 'Plan fulfillment' },
};

// Show toast notifications for tool call results
const showToolResultToasts = (toolCalls: AIToolCall[] | undefined) => {
  if (!toolCalls || toolCalls.length === 0) return;

  for (const tc of toolCalls) {
    const toolInfo = TOOL_DISPLAY_NAMES[tc.name];
    if (!toolInfo) continue; // Skip unknown tools

    if (tc.error) {
      // Show error toast
      toast.error(`${toolInfo.resource} error`, {
        description: tc.error,
      });
    } else if (tc.result?.success !== undefined) {
      // Only show success toast for mutation operations (create/update/delete)
      if (tc.result.success && tc.name.match(/^(create|update|delete)_/)) {
        toast.success(`${toolInfo.resource} ${toolInfo.action}`, {
          description: tc.result.message || `${toolInfo.resource} was ${toolInfo.action} successfully`,
        });
      } else if (!tc.result.success) {
        toast.error(`Failed to ${tc.name.replace('_', ' ')}`, {
          description: tc.result.message || 'An error occurred',
        });
      }
    }
  }
};

interface DbMessage {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant';
  content: string;
  tool_calls: Record<string, unknown>[] | null;
  tool_results: Record<string, unknown>[] | null;
  created_at: string;
}

export const useAIChat = (
  businessId: string | null,
  accessToken?: string
) => {
  const queryClient = useQueryClient();
  const [conversations, setConversations] = useState<AIConversationSummary[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [skipNextFetch, setSkipNextFetch] = useState(false); // Flag to skip fetch after creating new conversation
  const [usage, setUsage] = useState<AIUsage | null>(null);
  const [lastFailedMessage, setLastFailedMessage] = useState<string | null>(null);
  const [wasStopped, setWasStopped] = useState(false);
  const [streamingContent, setStreamingContent] = useState<string>('');
  const [useStreaming, setUseStreaming] = useState(false); // Feature flag for streaming
  const abortControllerRef = useRef<AbortController | null>(null);

  // Fetch conversations for the business
  const fetchConversations = useCallback(async () => {
    if (!businessId) {
      setConversations([]);
      return;
    }

    setIsLoadingConversations(true);
    try {
      const { data, error } = await supabase
        .from('ai_conversations')
        .select(`
          id,
          title,
          created_at,
          updated_at,
          ai_messages(count)
        `)
        .eq('business_id', businessId)
        .order('updated_at', { ascending: false });

      if (error) throw error;

      const summaries: AIConversationSummary[] = (data || []).map((conv: any) => ({
        id: conv.id,
        title: conv.title,
        createdAt: conv.created_at,
        updatedAt: conv.updated_at,
        messageCount: conv.ai_messages?.[0]?.count || 0,
      }));

      setConversations(summaries);

      // Auto-select first conversation if none active
      if (!activeConversationId && summaries.length > 0) {
        setActiveConversationId(summaries[0].id);
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
    } finally {
      setIsLoadingConversations(false);
    }
  }, [businessId, activeConversationId]);

  // Fetch messages for active conversation
  const fetchMessages = useCallback(async () => {
    if (!activeConversationId) {
      setMessages([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('ai_messages')
        .select('*')
        .eq('conversation_id', activeConversationId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const msgs: AIMessage[] = (data || []).map((msg: DbMessage) => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: msg.created_at,
        toolCalls: msg.tool_calls as AIToolCall[] | undefined,
        toolResults: msg.tool_results as AIToolResult[] | undefined,
      }));

      setMessages(msgs);
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  }, [activeConversationId]);

  // Process SSE streaming response
  const processStreamingResponse = useCallback(async (
    response: Response,
    assistantMessageId: string,
    currentConvId: string
  ): Promise<{ content: string; toolCalls: AIToolCall[]; toolResults: AIToolResult[]; pendingTools: PendingTool[] }> => {
    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let content = '';
    let toolCalls: AIToolCall[] = [];
    let toolResults: AIToolResult[] = [];
    let pendingTools: PendingTool[] = [];

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n').filter(line => line.startsWith('data: '));

        for (const line of lines) {
          const data = line.slice(6); // Remove 'data: ' prefix
          if (data === '[DONE]') continue;

          try {
            const event: StreamEvent = JSON.parse(data);

            switch (event.type) {
              case 'content':
                if (event.content) {
                  content += event.content;
                  setStreamingContent(content);
                  // Update message in real-time
                  setMessages(prev => prev.map(msg =>
                    msg.id === assistantMessageId
                      ? { ...msg, content }
                      : msg
                  ));
                }
                break;

              case 'tool_start':
                console.log('[AI Chat] Tool execution started:', event.tools);
                break;

              case 'tool_result':
                if (event.tool && event.result) {
                  toolResults.push(event.result);
                }
                break;

              case 'pending_tools':
                if (event.pendingTools) {
                  pendingTools = event.pendingTools;
                }
                break;

              case 'done':
                if (event.response) content = event.response;
                if (event.toolCalls) toolCalls = event.toolCalls;
                if (event.pendingTools) pendingTools = event.pendingTools;
                break;

              case 'error':
                throw new Error(event.error || 'Streaming error');
            }
          } catch (parseError) {
            // Ignore parse errors in stream - might be partial data
          }
        }
      }
    } finally {
      reader.releaseLock();
      setStreamingContent(''); // Clear streaming content to prevent memory leak
    }

    return { content, toolCalls, toolResults, pendingTools };
  }, []);

  // Load conversations when businessId changes
  useEffect(() => {
    if (businessId) {
      fetchConversations();
    } else {
      setConversations([]);
      setActiveConversationId(null);
      setMessages([]);
    }
  }, [businessId]);

  // Load messages when active conversation changes
  useEffect(() => {
    if (skipNextFetch) {
      setSkipNextFetch(false);
      return;
    }
    fetchMessages();
  }, [activeConversationId, fetchMessages, skipNextFetch]);

  // Create a new conversation
  const createNewConversation = useCallback(async () => {
    if (!businessId) return null;

    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return null;

      const { data, error } = await supabase
        .from('ai_conversations')
        .insert({
          business_id: businessId,
          user_id: userData.user.id,
          title: 'New Chat',
        })
        .select()
        .single();

      if (error) throw error;

      const newSummary: AIConversationSummary = {
        id: data.id,
        title: data.title,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        messageCount: 0,
      };

      setConversations(prev => [newSummary, ...prev]);
      setSkipNextFetch(true); // Prevent fetchMessages from running
      setActiveConversationId(data.id);
      setMessages([]);

      return data.id;
    } catch (err) {
      console.error('Error creating conversation:', err);
      toast.error('Failed to create new chat');
      return null;
    }
  }, [businessId]);

  // Switch to a different conversation
  const switchConversation = useCallback((conversationId: string) => {
    if (conversations.some(c => c.id === conversationId)) {
      setActiveConversationId(conversationId);
    }
  }, [conversations]);

  // Delete a conversation
  const deleteConversation = useCallback(async (conversationId: string) => {
    if (!businessId) return;

    try {
      const { error } = await supabase
        .from('ai_conversations')
        .delete()
        .eq('id', conversationId);

      if (error) throw error;

      setConversations(prev => prev.filter(c => c.id !== conversationId));

      // If we deleted the active conversation, switch to another
      if (activeConversationId === conversationId) {
        const remaining = conversations.filter(c => c.id !== conversationId);
        setActiveConversationId(remaining.length > 0 ? remaining[0].id : null);
      }
    } catch (err) {
      console.error('Error deleting conversation:', err);
      toast.error('Failed to delete conversation');
    }
  }, [businessId, conversations, activeConversationId]);

  // Send a message with optional structured answers
  const sendMessage = useCallback(
    async (content: string, structuredAnswers?: AIQuestionAnswers) => {
      if (!businessId) {
        toast.error('No business selected', {
          description: 'Please select a business first',
        });
        return;
      }

      if (!accessToken) {
        toast.error('Authentication required', {
          description: 'Please sign in to use the AI assistant',
        });
        return;
      }

      const trimmedContent = content.trim();
      if (!trimmedContent) return;

      if (trimmedContent.length > 2000) {
        toast.error('Message too long', {
          description: 'Please keep your message under 2000 characters',
        });
        return;
      }

      // Create new conversation if none active
      let currentConvId = activeConversationId;

      if (!currentConvId) {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) {
          toast.error('Authentication required');
          return;
        }

        try {
          const { data, error } = await supabase
            .from('ai_conversations')
            .insert({
              business_id: businessId,
              user_id: userData.user.id,
              title: generateTitle(trimmedContent),
            })
            .select()
            .single();

          if (error) throw error;

          currentConvId = data.id;
          const newSummary: AIConversationSummary = {
            id: data.id,
            title: data.title,
            createdAt: data.created_at,
            updatedAt: data.updated_at,
            messageCount: 0,
          };
          setConversations(prev => [newSummary, ...prev]);
          setSkipNextFetch(true); // Prevent fetchMessages from overwriting optimistic update
          setActiveConversationId(currentConvId);
        } catch (err) {
          console.error('Error creating conversation:', err);
          toast.error('Failed to start conversation');
          return;
        }
      }

      // Add user message to database
      const userMessageId = crypto.randomUUID();
      const userMessage: AIMessage = {
        id: userMessageId,
        role: 'user',
        content: trimmedContent,
        timestamp: new Date().toISOString(),
        questionAnswers: structuredAnswers, // Include raw answers if provided
      };

      // Optimistically add to UI
      setMessages(prev => [...prev, userMessage]);

      try {
        // Save user message to database
        const { error: msgError } = await supabase
          .from('ai_messages')
          .insert({
            id: userMessageId,
            conversation_id: currentConvId,
            role: 'user',
            content: trimmedContent,
          });

        if (msgError) throw msgError;

        // Update conversation title if first message
        const isFirstMessage = messages.length === 0;
        if (isFirstMessage) {
          await supabase
            .from('ai_conversations')
            .update({ title: generateTitle(trimmedContent) })
            .eq('id', currentConvId);

          // Update local state
          setConversations(prev =>
            prev.map(c =>
              c.id === currentConvId
                ? { ...c, title: generateTitle(trimmedContent) }
                : c
            )
          );
        }
      } catch (err) {
        console.error('Error saving user message:', err);
      }

      setIsLoading(true);
      setError(null);
      setWasStopped(false);
      setLastFailedMessage(trimmedContent);

      // Create abort controller for this request
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      try {
        const response = await fetch(AI_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: trimmedContent,
            businessId,
            conversationId: currentConvId,
            conversationHistory: [...messages, userMessage].slice(-10),
            structuredAnswers,
          }),
          signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }

        const data: AIResponse = await response.json();

        if (data?.error) {
          throw new Error(data.error);
        }

        const assistantMessageId = crypto.randomUUID();
        const assistantMessage: AIMessage = {
          id: assistantMessageId,
          role: 'assistant',
          content: data?.response || 'No response received',
          timestamp: new Date().toISOString(),
          toolCalls: data?.toolCalls,
          toolResults: data?.toolResults, // Structured tool results for proper history
          pendingTools: data?.pendingTools,
          questions: data?.questions, // Include structured questions from ask_user tool
        };

        // Track usage stats
        if (data?.usage) {
          setUsage(data.usage);
        }

        // Add to UI
        setMessages(prev => [...prev, assistantMessage]);

        // Show toast notifications for tool call results
        showToolResultToasts(data?.toolCalls);

        // Note: Auto-navigation removed - users can click "Show" button to navigate manually

        // Invalidate caches if data was modified by tool calls
        const modifiedResources = getModifiedResources(data?.toolCalls);

        if (modifiedResources.shifts && businessId) {
          // Invalidate all shift queries for this business
          queryClient.invalidateQueries({
            queryKey: shiftQueryKeys.all,
            predicate: (query) => {
              const key = query.queryKey;
              return Array.isArray(key) && key.includes(businessId);
            },
          });
          console.log('[AI Chat] Shift data invalidated - triggering refetch');
        }

        if (modifiedResources.employees && businessId) {
          // Invalidate employee-related queries
          queryClient.invalidateQueries({
            queryKey: ['employees', businessId],
          });
          queryClient.invalidateQueries({
            queryKey: ['employment-types', businessId],
          });
          console.log('[AI Chat] Employee data invalidated - triggering refetch');
        }

        // Save to database (includes tool_results for proper conversation history)
        await supabase
          .from('ai_messages')
          .insert({
            id: assistantMessageId,
            conversation_id: currentConvId,
            role: 'assistant',
            content: assistantMessage.content,
            tool_calls: data?.toolCalls || null,
            tool_results: data?.toolResults || null,
          });

        // Update conversation summary
        setConversations(prev =>
          prev.map(c =>
            c.id === currentConvId
              ? { ...c, updatedAt: new Date().toISOString(), messageCount: c.messageCount + 2 }
              : c
          )
        );

        // Clear failed message on success
        setLastFailedMessage(null);
      } catch (err) {
        // Check if this was an abort (user stopped the request)
        const isAborted = err instanceof Error && err.name === 'AbortError';

        if (isAborted) {
          setWasStopped(true);
          setError('Request stopped');
          // Don't show toast for user-initiated stop
          return;
        }

        const errorMessage =
          err instanceof Error ? err.message : 'An unexpected error occurred';
        setError(errorMessage);
        toast.error('AI Error', { description: errorMessage });

        const errorMessageId = crypto.randomUUID();
        const errorResponse: AIMessage = {
          id: errorMessageId,
          role: 'assistant',
          content: `Sorry, I encountered an error: ${errorMessage}. Please try again.`,
          timestamp: new Date().toISOString(),
        };

        setMessages(prev => [...prev, errorResponse]);

        // Save error response to database
        await supabase
          .from('ai_messages')
          .insert({
            id: errorMessageId,
            conversation_id: currentConvId,
            role: 'assistant',
            content: errorResponse.content,
          });
      } finally {
        setIsLoading(false);
        abortControllerRef.current = null; // Clear to prevent stale reference
      }
    },
    [businessId, accessToken, activeConversationId, messages]
  );

  // Confirm and execute pending tools
  const confirmTools = useCallback(
    async (pendingTools: PendingTool[]) => {
      // Send a confirmation message that will trigger the pending tools
      const toolDescriptions = pendingTools.map(t => t.description).join(', ');
      await sendMessage(`Yes, proceed with: ${toolDescriptions}`);
    },
    [sendMessage]
  );

  // Send answers to structured questions
  const sendQuestionAnswers = useCallback(
    async (answers: AIQuestionAnswers, displayText: string) => {
      // Send message with both display text and raw answers
      await sendMessage(displayText, answers);
    },
    [sendMessage]
  );

  // Clear current conversation messages
  const clearMessages = useCallback(async () => {
    if (!businessId || !activeConversationId) return;

    try {
      // Delete all messages in the conversation
      const { error } = await supabase
        .from('ai_messages')
        .delete()
        .eq('conversation_id', activeConversationId);

      if (error) throw error;

      setMessages([]);

      // Update conversation summary
      setConversations(prev =>
        prev.map(c =>
          c.id === activeConversationId
            ? { ...c, messageCount: 0 }
            : c
        )
      );
    } catch (err) {
      console.error('Error clearing messages:', err);
      toast.error('Failed to clear messages');
    }
    setError(null);
  }, [businessId, activeConversationId]);

  // Stop the current AI generation
  const stopGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      console.log('[AI Chat] Stopping generation...');
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
    }
  }, []);

  // Retry the last failed/stopped message
  const retryLastMessage = useCallback(() => {
    if (lastFailedMessage) {
      console.log('[AI Chat] Retrying last message:', lastFailedMessage);
      setError(null);
      setWasStopped(false);
      sendMessage(lastFailedMessage);
    }
  }, [lastFailedMessage, sendMessage]);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
    confirmTools,
    sendQuestionAnswers, // For structured question form submissions
    // Conversation management
    conversationSummaries: conversations,
    activeConversationId,
    createNewConversation,
    switchConversation,
    deleteConversation,
    isLoadingConversations,
    // Usage stats
    usage,
    // Stop and retry
    stopGeneration,
    retryLastMessage,
    canRetry: !!lastFailedMessage && (!!error || wasStopped),
    wasStopped,
    // Streaming
    streamingContent,
    useStreaming,
    setUseStreaming,
  };
};

export default useAIChat;
